import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { AusverkaufAbzeichen } from '@/components/AusverkaufAbzeichen';
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useSuppliers, useGaSearch, usePurchaseNotes } from "@/api/dashboard";
import type {
  BatchCreateOrdersInput, BatchCreatedOrder, PaarFrage, VorschlagPosition, VorschlagResponse,
} from "@/api/purchase";
import { bestellungenAnlegen, pruefeBestellnummer, neueBatchId, vorschlagAusDatei } from "./bestellnummer";
import { fehlerText, fachlicherStatus } from "@/lib/fehlerText";

/**
 * A3 — „+ Neue Bestellung" als Assistent (Modul 38, Block B).
 *
 * Vorher legte der Knopf sofort eine LEERE Bestellung an (`POST /purchase/orders/` mit `{}`)
 * und sprang in die Akte: ohne Lieferant, ohne Datum, ohne Positionen — ein Datensatz, der
 * erst nachtraeglich zu einer Bestellung wurde und bis dahin in jeder Liste mitzaehlte. Auf
 * Prod stehen genau so drei solche Karteileichen.
 *
 * Jetzt entsteht die Bestellung erst beim Bestaetigen, in EINEM Aufruf mit ihren Positionen.
 *
 * DREI DINGE, DIE HIER FACHLICH TRAGEN:
 *
 * 1. **`client_batch_id` gilt fuer EINE Nutzlast, nicht fuer eine Sitzung.** Sie entsteht
 *    beim Oeffnen des Dialogs (nicht beim Rendern der Seite — der Assistent haengt dauerhaft
 *    in der Uebersicht) und bleibt bei einem WIEDERHOLTEN Versuch derselben Eingaben gleich:
 *    zweiter Klick, verlorene Antwort, Retry nach Netzabbruch treffen damit dieselbe
 *    Bestellung (Server: 200, `wiederholung: true`). Aendert der Nutzer nach einem
 *    Fehlversuch etwas, entsteht eine NEUE Id — sonst bekaeme er auf geaenderte Eingaben die
 *    alte Bestellung zurueck oder einen Idempotenz-Konflikt. (Beides Befunde von
 *    Reviewer 1, angenommen.)
 * 2. **„bestellt" verlangt Lieferant UND mindestens eine Position.** Das Backend lehnt es
 *    ohnehin ab; die Oberflaeche sagt es vorher, statt den Nutzer in einen 400 laufen zu
 *    lassen.
 * 3. **Keine Nummern-Vorschau.** Fuer automatische Nummern steht „wird automatisch vergeben"
 *    — jeder angezeigte Wert waere entweder geraten oder eine Reservierung, die niemand
 *    einloest (Design v2 §7.1, Codex-Befund 9).
 */

interface PositionsZeile {
  ausverkauf?: boolean;
  ausverkauf_quelle?: 'dr' | 'hand' | null;
  /** Plan 2026-10-21 (A2): derselbe Artikel darf mehrfach stehen (eigener EK je Position) —
   *  Tabelle, Menge, Notiz und Entfernen laufen deshalb ueber diesen Schluessel. */
  key: string;
  stock_item_id: number;
  merchant_sku: string | null;
  name: string;
  qty: number;
  /** Aus der Datei erkannter EK. null bedeutet: nicht vorhanden oder keine Preisberechtigung. */
  unit_price_usd: number | null;
  notiz: string;
  /** Bestimmt die Audit-Quelle des Batch-Vorgangs; wird nicht als Positionsfeld gesendet. */
  aus_datei: boolean;
  /** Vormerkung, aus der diese Zeile stammt — wird beim Anlegen abgeloest. */
  note_id?: number;
  /** Plan 2026-10-20 (B3): Herkunft einer Set-Zeile aus der Datei. Der Server rechnet mit dem
   *  aktuellen Faktor des Grundartikels nach; eine Handkorrektur der Menge loest die Bindung. */
  set_bindung?: SetBindung | null;
  /** Review 2026-10-21 Runde 2: die Zeile stammt aus einem Vorschlag, der `set_einheit` kennt
   *  (Backend ab B-2). Nur dann gilt der Datei-Vertrag 2. */
  vertrag_2?: boolean;
  /** Plan 2026-10-21 (B3): pcs-/Stueckzeile ausdruecklich als Paare/Sets bestaetigt. */
  paar_bestaetigt?: boolean;
}

interface SetBindung {
  source_qty: number;
  source_unit: string;
  source_unit_price_usd: number | null;
  stueck_je_set: number;
}

const heute = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** DB und Importvertrag tragen bis zu vier Nachkommastellen; HAO nutzt z. B. 1,466 USD. */
const formatStueckpreisUsd = (wert: number): string =>
  wert.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/** Ganze Stueckzahlen mit Tausenderpunkt — „3.000", nicht „3000". */
const formatMenge = (wert: number): string => wert.toLocaleString("de-DE");

/**
 * Modul 43 — die Umrechnung einer Zeile als NACHRECHENBARER Satz.
 *
 * Bewusst die ganze Kette statt nur des Ergebnisses: „1.500 set × 2 = 3.000 Stück;
 * 0,926 USD/set ÷ 2 = 0,463 USD/Stück; Betrag 1.389,00 USD unverändert". Wer nur
 * „3.000 Stück" liest, kann nicht pruefen, ob der Faktor stimmt — und genau das ist
 * die Entscheidung, die der Einkaeufer hier treffen soll. Die Zahlen kommen alle vom
 * Server; hier wird nichts nachgerechnet, sonst driften zwei Rechenwege auseinander.
 */
function umrechnungsSatz(pos: {
  source_qty?: number | null;
  source_unit?: string | null;
  unit_factor?: number | null;
  qty: number | null;
  source_unit_price_usd?: number | null;
  unit_price_usd: number | null;
  source_amount_usd?: number | null;
}): string {
  const einheit = pos.source_unit ?? "";
  const faktor = pos.unit_factor ?? 1;
  const teile: string[] = [];
  if (pos.source_qty !== null && pos.source_qty !== undefined && pos.qty !== null) {
    teile.push(`${formatMenge(pos.source_qty)} ${einheit} × ${faktor} = ${formatMenge(pos.qty)} Stück`);
  }
  const quellPreis = pos.source_unit_price_usd;
  if (quellPreis !== null && quellPreis !== undefined && pos.unit_price_usd !== null) {
    teile.push(
      `${formatStueckpreisUsd(quellPreis)} USD/${einheit} ÷ ${faktor} = `
      + `${formatStueckpreisUsd(pos.unit_price_usd)} USD/Stück`,
    );
  }
  const betrag = pos.source_amount_usd;
  if (betrag !== null && betrag !== undefined) {
    teile.push(`Betrag ${formatStueckpreisUsd(betrag)} USD unverändert`);
  }
  return teile.join("; ");
}

/** Plan 2026-10-21 (B3): Wortlaut der Paar-Frage nach dem Faktor des Grundartikels. */
function paarWorte(n: number): { frage: string; einheit: string; ja: string } {
  return n === 2
    ? { frage: "Paare gezählt?", einheit: "Paar = 2 Stück", ja: "Ja, Paare (× 2)" }
    : { frage: "Sets gezählt?", einheit: `Set = ${n} Stück`, ja: `Ja, Sets (× ${n})` };
}

export function NeueBestellungAssistent({
  offen,
  onClose,
  onAngelegt,
}: {
  offen: boolean;
  onClose: () => void;
  onAngelegt: (order: BatchCreatedOrder) => void;
}) {
  const { data: suppliersData } = useSuppliers();
  const suppliers = suppliersData?.suppliers ?? [];

  const [schritt, setSchritt] = useState<1 | 2 | 3>(1);
  const [batchId, setBatchId] = useState<string>(() => neueBatchId());
  /** Nutzlast des letzten Sendeversuchs — entscheidet, ob die Id gilt oder neu muss. */
  const versuchteNutzlast = useRef<string | null>(null);
  /**
   * Zaehlt jedes Oeffnen/Schliessen mit. Eine Antwort, die zu einem SPAETEREN Dialog
   * gehoert, darf nichts mehr ausloesen — sonst schliesst die Antwort eines abgebrochenen
   * Versuchs den frisch geoeffneten Dialog und springt in die alte Bestellung.
   * (Befund von Reviewer 1, angenommen.)
   */
  const generation = useRef(0);
  /** Eigene Generation nur fuer Datei-Vorschlaege. Lieferantenwechsel und ein
   *  neuer Upload duerfen alte Vorschlaege verwerfen, aber niemals die Antwort
   *  einer bereits laufenden Bestellanlage unsichtbar machen. */
  const vorschlagGeneration = useRef(0);
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [orderDate, setOrderDate] = useState<string>(heute);
  const [nacherfassung, setNacherfassung] = useState(false);
  const [nummer, setNummer] = useState("");
  const [status, setStatus] = useState<"entwurf" | "bestellt">("bestellt");
  const [notiz, setNotiz] = useState("");
  const [zeilen, setZeilen] = useState<PositionsZeile[]>([]);
  const [suche, setSuche] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  // Schritt 3: Vorschlag aus einer Datei. Nichts davon ist gespeichert — der Server liest
  // nur und schlaegt vor; angelegt wird ausschliesslich ueber den Batch-Weg in Schritt 2.
  const [vorschlag, setVorschlag] = useState<VorschlagResponse | null>(null);
  const [vorschlagLaeuft, setVorschlagLaeuft] = useState(false);
  /**
   * Meldung zur Auswertung — mit ihrer ART, nicht nur ihrem Text.
   *
   * Der Unterschied ist keine Kosmetik: 413/415/422 sind Befunde an der DATEI DES NUTZERS
   * (er muss etwas aendern → `alert`), 501 ist ein BAUZUSTAND der Plattform (der
   * PDF-Auswerter kommt erst → `status`). Als roter Fehler dargestellt suchte der Nutzer
   * die Schuld bei sich und probierte dieselbe Datei mehrfach.
   */
  const [vorschlagMeldung, setVorschlagMeldung] =
    useState<{ text: string; art: "fehler" | "hinweis" } | null>(null);
  /** Nutzerwahl je Vorschlagszeile: welcher Kandidat gilt (Pflicht bei match "none"). */
  const [wahl, setWahl] = useState<Record<number, number | null>>({});
  /** Name der zuletzt gewaehlten Datei — der native Knopf zeigt ihn nicht mehr an,
   *  sobald er versteckt ist, und ohne ihn weiss niemand, was gerade ausgewertet wurde. */
  const [dateiName, setDateiName] = useState<string | null>(null);
  /** Modul 43: der Einkaeufer hat die Mengenumrechnung DIESER Datei ausdruecklich
   *  bestaetigt. Gilt unbefristet fuer die Datei und faellt erst mit der naechsten. */
  const [einheitBestaetigt, setEinheitBestaetigt] = useState(false);
  /** Plan 2026-10-21 (B3): Antwort je Vorschlagszeile auf „Paare gezählt?". Gehoert zur Datei
   *  und zur Artikelwahl der Zeile — beides neu, Antwort weg. Nie vorbelegt. */
  const [paarAntwort, setPaarAntwort] = useState<Record<number, "paar" | "stueck">>({});
  /** Der native Dateidialog wird ueber diesen Verweis geoeffnet — das Eingabefeld
   *  selbst bleibt im Baum (und bedienbar), nur unsichtbar. */
  const dateiFeld = useRef<HTMLInputElement | null>(null);

  // Beim OEFFNEN eine frische Id: der Assistent ist dauerhaft eingehaengt, die Id aus dem
  // ersten Rendern der Seite waere sonst noch Stunden spaeter in Gebrauch.
  useEffect(() => {
    if (!offen) return;
    generation.current += 1;
    vorschlagGeneration.current += 1;
    setBatchId(neueBatchId());
    versuchteNutzlast.current = null;
    setLaeuft(false);
    setVorschlagLaeuft(false);
  }, [offen]);

  const { data: suchErgebnis } = useGaSearch(suche);
  const { data: notesData } = usePurchaseNotes("open");
  const offeneVormerkungen = notesData?.notes ?? [];

  const nummerPruefung = useMemo(
    () => (nacherfassung ? pruefeBestellnummer(nummer) : { gueltig: true as const }),
    [nacherfassung, nummer],
  );

  function zuruecksetzen() {
    setSchritt(1);
    setBatchId(neueBatchId());
    setSupplierId(null);
    setOrderDate(heute());
    setNacherfassung(false);
    setNummer("");
    setStatus("bestellt");
    setNotiz("");
    setZeilen([]);
    setSuche("");
    setVorschlag(null);
    setVorschlagMeldung(null);
    setWahl({});
    setDateiName(null);
    setEinheitBestaetigt(false);
    setPaarAntwort({});
    setLaeuft(false);
    setVorschlagLaeuft(false);
    versuchteNutzlast.current = null;
  }

  function schliessen() {
    // Generation hochzaehlen: eine noch laufende Antwort gehoert ab jetzt zu niemandem mehr.
    generation.current += 1;
    vorschlagGeneration.current += 1;
    zuruecksetzen();
    onClose();
  }

  function zeileHinzu(z: PositionsZeile) {
    setZeilen((alt) => {
      if (alt.some((a) => a.stock_item_id === z.stock_item_id)) {
        // Sichtbar abweisen statt still schlucken (Befund von Reviewer 1): der Vertrag lehnt
        // doppelte `stock_item_id` mit 400 ab, ein zweiter Klick ist aber ein normaler Griff.
        toast.info(`${z.merchant_sku ?? z.name} steht schon in der Liste`);
        return alt;
      }
      return [...alt, z];
    });
  }

  /**
   * Lieferantenprofile bestimmen Nummernraum, Set-Faktor und Preiseinheit einer
   * Dateiauswertung. Beim Wechsel darf deshalb weder eine fertige noch eine noch
   * laufende HAO-Auswertung weiterleben. Auch bereits uebernommene Positionen
   * werden entfernt: ihre normalisierten Werte tragen ihre Herkunft nicht mehr
   * einzeln und koennten sonst unter dem neuen Lieferanten angelegt werden.
   */
  function lieferantWechseln(neu: number | null) {
    if (neu === supplierId) return;
    const hatteDaten = zeilen.length > 0 || vorschlag !== null || vorschlagLaeuft || dateiName !== null;
    vorschlagGeneration.current += 1;
    setSupplierId(neu);
    setZeilen([]);
    setVorschlag(null);
    setWahl({});
    setVorschlagMeldung(null);
    setDateiName(null);
    setEinheitBestaetigt(false);
    setPaarAntwort({});
    setVorschlagLaeuft(false);
    if (hatteDaten) {
      toast.info("Positionen und Dateiauswertung wurden wegen des Lieferantenwechsels zurückgesetzt");
    }
  }

  const kopfFehlt =
    supplierId === null || orderDate === "" || (nacherfassung && !nummerPruefung.gueltig);

  // „bestellt" ohne Position waere eine Bestellung, die nichts bestellt — das Backend lehnt
  // sie ab (Design §7.1), also fragt die Oberflaeche gar nicht erst danach.
  const mengeOk = (z: PositionsZeile): boolean => Number.isSafeInteger(z.qty) && z.qty > 0;
  const ungueltigeMengen = zeilen.filter((z) => !mengeOk(z));
  const darfAbsenden = !kopfFehlt && zeilen.length > 0 && ungueltigeMengen.length === 0;

  /** Liest die gewaehlte Datei ein und holt den Vorschlag. Speichert nichts. */
  async function dateiAuswerten(datei: File) {
    // Die alte Vorschau MUSS sofort weg. Sonst stuende nach einer abgelehnten zweiten
    // Datei die 501-Meldung ueber der Vorschau der ERSTEN — und der Nutzer uebernaehme
    // Positionen aus einer Datei, die er gar nicht mehr meint. (Befund beider Reviewer.)
    setVorschlag(null);
    setWahl({});
    setVorschlagLaeuft(true);
    setVorschlagMeldung(null);
    setDateiName(datei.name);
    // Die Bestaetigung gehoert zur DATEI, nicht zur Sitzung: eine neue Datei bringt
    // neue Zeilen und muss neu bestaetigt werden. Sonst uebernaehme ein zweiter
    // Upload seine Umrechnung unter der Zustimmung zum ersten.
    setEinheitBestaetigt(false);
    setPaarAntwort({});
    // Dieselbe Sicherung wie im Anlege-Weg (`absenden`): eine verspaetete Antwort darf
    // einen inzwischen geschlossenen oder neu geoeffneten Assistenten nicht mehr fuellen.
    const meineGeneration = generation.current;
    const meineVorschlagGeneration = ++vorschlagGeneration.current;
    try {
      // Modul 43: der in Schritt 1 gewaehlte Lieferant geht mit. Er entscheidet
      // serverseitig, ob ein Lieferantenprofil greift (Nummernraum, Mengeneinheiten).
      // Ohne ihn bleibt es bei der bisherigen, sicheren Mehrdeutigkeit.
      const antwort = await vorschlagAusDatei(datei, supplierId);
      if (meineGeneration !== generation.current
        || meineVorschlagGeneration !== vorschlagGeneration.current) return;
      setVorschlag(antwort);
      // NUR eindeutige Treffer sind vorbelegt; alles andere braucht eine Entscheidung.
      // Der Vertrag setzt `stock_item_id` zwar nur bei `exact` — hier wird trotzdem
      // ausdruecklich auf `match` geprueft: ein Vorbelegen aus einem unsicheren Treffer waere
      // genau die stille Automatik, die Design §7.1 verbietet, und ein Vertrauen auf eine
      // fremde Zusage ist kein Schutz. (Befund von Reviewer 1, angenommen.)
      const vorbelegt: Record<number, number | null> = {};
      for (const pos of antwort.positionen) {
        vorbelegt[pos.zeile] = pos.match === "exact" ? pos.stock_item_id : null;
      }
      setWahl(vorbelegt);
    } catch (e: unknown) {
      if (meineGeneration !== generation.current
        || meineVorschlagGeneration !== vorschlagGeneration.current) return;
      // Das Backend beantwortet jede Sperre mit einem SATZ (routes.ts: „Datei zu gross
      // (max. 25 MB).", „PDF wird noch nicht ausgewertet…"). Ihn wegzuwerfen und einen
      // eigenen Text zu zeigen hiesse, den Nutzer die Ursache raten zu lassen.
      const status = fachlicherStatus(e);
      setVorschlagMeldung({
        text: fehlerText(
          e,
          "Die Datei konnte nicht ausgewertet werden. Nichts wurde gespeichert — bitte Format prüfen oder von Hand erfassen.",
        ),
        art: status === 501 ? "hinweis" : "fehler",
      });
    } finally {
      if (meineGeneration === generation.current
        && meineVorschlagGeneration === vorschlagGeneration.current) setVorschlagLaeuft(false);
    }
  }

  /** Modul 43: Zeilen, die der Server gesperrt hat — unbekannte Mengeneinheit oder
   *  verletzte Betragsinvariante. Sie werden NICHT uebernommen UND sie halten die
   *  ganze Uebernahme an: wer eine Zeile nicht deuten kann, hat die Datei nicht
   *  verstanden, und die uebrigen Zeilen derselben Datei sind dann nur vermutlich
   *  richtig. Lieber eine Rueckfrage beim Lieferanten als eine halbe Bestellung. */
  const gesperrteZeilen = (vorschlag?.positionen ?? []).filter((pos) => pos.blocked === true);
  /** Zeilen, die uebernommen werden koennen: nur mit zugeordnetem Artikel und Menge > 0. */
  const uebernehmbar = (vorschlag?.positionen ?? []).filter(
    (pos) => pos.blocked !== true && (wahl[pos.zeile] ?? null) !== null && (pos.qty ?? 0) > 0,
  );
  const offeneZuordnungen =
    (vorschlag?.positionen ?? []).length - uebernehmbar.length - gesperrteZeilen.length;
  /** Zeilen mit echter Umrechnung (Faktor ungleich 1) — sie brauchen die Bestaetigung. */
  const umgerechneteZeilen = (vorschlag?.positionen ?? []).filter(
    (pos) => pos.requires_unit_confirmation === true,
  );
  const brauchtBestaetigung = umgerechneteZeilen.length > 0;
  // Ohne article_purchase_prices liefert der Server ausschliesslich null. Dann erscheint
  // auch in der Oberflaeche kein Preisfeld (Design §121), nicht einmal eine leere Spalte.
  const vorschlagHatPreise = (vorschlag?.positionen ?? []).some((pos) => pos.unit_price_usd !== null);
  const zeilenHabenPreise = zeilen.some((z) => z.unit_price_usd !== null);
  /** Plan 2026-10-21 (B1/B3): die Paar-Frage zur GEWAEHLTEN Zuordnung der Zeile — bei
   *  eindeutigem Treffer die der Position, bei Hand-Wahl die des Kandidaten. */
  const paarFrageFuer = (pos: VorschlagPosition): PaarFrage | null => {
    const id = wahl[pos.zeile] ?? null;
    if (id === null || pos.blocked === true) return null;
    if (pos.stock_item_id === id) return pos.paar_frage ?? null;
    return pos.kandidaten.find((k) => k.stock_item_id === id)?.paar_frage ?? null;
  };
  const paarFragen = uebernehmbar.filter((pos) => paarFrageFuer(pos) !== null);
  const offenePaarFragen = paarFragen.filter((pos) => paarAntwort[pos.zeile] === undefined);
  /** Sammelknopf je Faktorwert (nie gemischt, wie im WE #140). Zeilen, deren Preis fuer Stueck
   *  spricht, bleiben aussen vor — sie brauchen den Einzelklick. */
  const sammelGruppen = (() => {
    const jeFaktor = new Map<number, number[]>();
    for (const pos of offenePaarFragen) {
      const frage = paarFrageFuer(pos)!;
      if (frage.empfehlung === "stueck" || frage.als_paar === null) continue;
      jeFaktor.set(frage.stueck_je_set, [...(jeFaktor.get(frage.stueck_je_set) ?? []), pos.zeile]);
    }
    return [...jeFaktor.entries()].sort(([a], [b]) => a - b);
  })();

  /** Plan 2026-10-21 (A2): Lieferantenrechnungen fuehren denselben Artikel in mehreren Zeilen
   *  (HAO: *064 zweimal mit eigenem EK). Gleicher Artikel + gleicher EK + gleiche Set-Bindung
   *  wird summiert; sonst entsteht je Variante eine eigene Position — der Misch-EK ergibt sich
   *  beim Einlagern gewichtet. Zusammenfassen ueber verschiedene EKs waere eine Geldentscheidung. */
  const vorbereiteteUebernahme = (() => {
    const jeSchluessel = new Map<string, PositionsZeile>();
    for (const pos of uebernehmbar) {
      const id = wahl[pos.zeile]!;
      const kandidat = pos.kandidaten.find((k) => k.stock_item_id === id);
      const frage = paarFrageFuer(pos);
      const alsPaar = frage !== null && frage.als_paar !== null && paarAntwort[pos.zeile] === "paar";
      const quellMenge = pos.source_qty ?? pos.qty;
      const bindung: SetBindung | null = frage !== null && alsPaar && quellMenge != null
        ? {
            source_qty: quellMenge,
            source_unit: pos.source_unit ?? "Stück",
            source_unit_price_usd: pos.source_unit_price_usd ?? pos.unit_price_usd ?? null,
            stueck_je_set: frage.stueck_je_set,
          }
        : pos.set_einheit && pos.unit_factor != null && pos.source_qty != null && pos.source_unit
          ? {
              source_qty: pos.source_qty,
              source_unit: pos.source_unit,
              source_unit_price_usd: pos.source_unit_price_usd ?? null,
              stueck_je_set: pos.unit_factor,
            }
          : null;
      const preis = frage?.als_paar && alsPaar ? frage.als_paar.unit_price_usd : pos.unit_price_usd;
      const schluessel = [
        id, preis ?? "-",
        bindung ? `${bindung.source_unit}|${bindung.stueck_je_set}|${bindung.source_unit_price_usd ?? "-"}` : "-",
        alsPaar ? "paar" : "",
      ].join("|");
      const zeile: PositionsZeile = {
        key: `datei:${schluessel}`,
        stock_item_id: id,
        merchant_sku: kandidat?.merchant_sku ?? pos.raw_sku,
        name: kandidat?.name ?? pos.raw_name ?? "",
        qty: frage?.als_paar && alsPaar ? frage.als_paar.qty : pos.qty ?? 1,
        unit_price_usd: preis,
        notiz: "",
        aus_datei: true,
        vertrag_2: pos.set_einheit !== undefined,
        set_bindung: bindung,
        ...(alsPaar ? { paar_bestaetigt: true } : {}),
      };
      const vorhanden = jeSchluessel.get(schluessel);
      if (!vorhanden) {
        jeSchluessel.set(schluessel, zeile);
        continue;
      }
      vorhanden.qty += zeile.qty;
      if (vorhanden.set_bindung && bindung) {
        vorhanden.set_bindung = { ...vorhanden.set_bindung, source_qty: vorhanden.set_bindung.source_qty + bindung.source_qty };
      }
    }
    const zeilen = [...jeSchluessel.values()];
    const jeArtikel = new Map<number, PositionsZeile[]>();
    for (const z of zeilen) jeArtikel.set(z.stock_item_id, [...(jeArtikel.get(z.stock_item_id) ?? []), z]);
    const mehrfach = [...jeArtikel.values()]
      .filter((teile) => teile.length > 1)
      .map((teile) => {
        const preise = teile.map((t) => (t.unit_price_usd === null ? "—" : formatStueckpreisUsd(t.unit_price_usd)));
        const ek = teile.some((t) => t.unit_price_usd !== null) ? `, EK ${preise.join(" / ")}` : "";
        return `${teile[0]!.merchant_sku ?? teile[0]!.name}: ${teile.length} Positionen${ek}`;
      });
    return {
      zeilen,
      zusammengefassteZeilen: uebernehmbar.length - zeilen.length,
      mehrfach,
    };
  })();

  /** Modul 43: alles, was die Uebernahme anhaelt — in EINER Bedingung, damit Knopf
   *  und Klickweg nicht auseinanderlaufen koennen. */
  const uebernahmeGesperrt =
    vorbereiteteUebernahme.zeilen.length === 0
    || gesperrteZeilen.length > 0
    || offenePaarFragen.length > 0
    || (brauchtBestaetigung && !einheitBestaetigt);

  /** Uebernimmt die entschiedenen Zeilen in Schritt 2 — angelegt wird erst dort. */
  function vorschlagUebernehmen() {
    if (gesperrteZeilen.length > 0) {
      toast.error(
        `${gesperrteZeilen.length} Position(en) konnten nicht sicher gedeutet werden — bitte Datei prüfen`,
      );
      return;
    }
    if (brauchtBestaetigung && !einheitBestaetigt) {
      toast.error("Bitte die Mengenumrechnung zuerst bestätigen");
      return;
    }
    if (offenePaarFragen.length > 0) {
      toast.error("Bitte zuerst beantworten, ob Paare oder Stück gezählt wurden");
      return;
    }
    const vorhandeneIds = new Set(zeilen.map((z) => z.stock_item_id));
    const neueZeilen = vorbereiteteUebernahme.zeilen.filter((z) => !vorhandeneIds.has(z.stock_item_id));
    const schonVorhanden = vorbereiteteUebernahme.zeilen.length - neueZeilen.length;
    setZeilen((alt) => [...alt, ...neueZeilen]);
    if (schonVorhanden > 0) {
      toast.info(`${schonVorhanden} bereits vorhandene Position(en) wurden nicht doppelt aufgenommen`);
    }
    const zusammenfassung = vorbereiteteUebernahme.zusammengefassteZeilen > 0
      ? `; ${vorbereiteteUebernahme.zusammengefassteZeilen} doppelte Dateizeile(n) summiert`
      : "";
    toast.success(`${neueZeilen.length} Positionen übernommen${zusammenfassung}`);
    setSchritt(2);
  }

  async function absenden() {
    if (!darfAbsenden || supplierId === null) return;
    // Die Nutzlast OHNE Id bildet den Vergleichswert: gleiche Eingaben = dieselbe Id
    // (Wiederholung), geaenderte Eingaben = neue Id (neue Bestellung).
    const inhalt = JSON.stringify({
      supplierId, orderDate, nummer: nacherfassung ? nummer.trim() : null, status,
      notiz: notiz.trim(), zeilen,
    });
    let id = batchId;
    if (versuchteNutzlast.current !== null && versuchteNutzlast.current !== inhalt) {
      id = neueBatchId();
      setBatchId(id);
    }
    versuchteNutzlast.current = inhalt;
    const meineGeneration = generation.current;
    const anfrage: BatchCreateOrdersInput = {
      client_batch_id: id,
      source: zeilen.some((z) => z.aus_datei) ? "datei" : "hand",
      // Review 2026-10-21 (Befund 2): dieser Assistent sendet die Set-Herkunft (Vertrag 2).
      // Ein Vorschlag des alten Backends (ohne `set_einheit`) traegt keine Set-Herkunft: dann kein
      // Vertrag 2, der neue Server verlangt Neuladen statt alte ×2-Mengen ungeprueft anzulegen.
      ...(zeilen.some((z) => z.aus_datei) && zeilen.every((z) => !z.aus_datei || z.vertrag_2 === true)
        ? { datei_vertrag: 2 }
        : {}),
      groups: [
        {
          supplier_id: supplierId,
          order_date: orderDate,
          ...(nacherfassung && nummer.trim() !== "" ? { order_number: nummer.trim() } : {}),
          status,
          ...(notiz.trim() !== "" ? { note: notiz.trim() } : {}),
          items: zeilen.map((z) => ({
            stock_item_id: z.stock_item_id,
            qty: z.qty,
            ...(z.unit_price_usd !== null ? { unit_price_usd: z.unit_price_usd } : {}),
            ...(z.notiz.trim() !== "" ? { anforderungs_notiz: z.notiz.trim() } : {}),
            ...(z.set_bindung
              ? {
                  source_qty: z.set_bindung.source_qty,
                  source_unit: z.set_bindung.source_unit,
                  stueck_je_set: z.set_bindung.stueck_je_set,
                  ...(z.set_bindung.source_unit_price_usd !== null
                    ? { source_unit_price_usd: z.set_bindung.source_unit_price_usd }
                    : {}),
                  ...(z.paar_bestaetigt ? { paar_bestaetigt: true } : {}),
                }
              : {}),
          })),
          ...(zeilen.some((z) => z.note_id)
            ? { note_ids: zeilen.filter((z) => z.note_id).map((z) => z.note_id!) }
            : {}),
        },
      ],
    };
    setLaeuft(true);
    try {
      const antwort = await bestellungenAnlegen(anfrage);
      // Gehoert die Antwort noch zu DIESEM Dialog? Wurde zwischenzeitlich abgebrochen und
      // neu geoeffnet, darf sie weder melden noch navigieren.
      if (meineGeneration !== generation.current) return;
      const angelegt = antwort.orders[0];
      if (!angelegt) throw new Error("Antwort ohne Bestellung");
      toast.success(
        antwort.wiederholung
          ? `Bestellung ${angelegt.order_number} bestand bereits — nichts doppelt angelegt`
          : `Bestellung ${angelegt.order_number} mit ${angelegt.item_count} Positionen angelegt`,
      );
      onAngelegt(angelegt);
      schliessen();
    } catch {
      if (meineGeneration !== generation.current) return;
      // Der Dialog BLEIBT offen und behaelt seine batchId, SOLANGE die Eingaben dieselben
      // sind: ein zweiter Versuch trifft dann dieselbe Bestellung statt eine zweite anzulegen.
      toast.error("Bestellung konnte nicht angelegt werden — Eingaben bleiben erhalten");
    } finally {
      if (meineGeneration === generation.current) setLaeuft(false);
    }
  }

  return (
    <Dialog open={offen} onOpenChange={(o) => { if (!o) schliessen(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90dvh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Neue Bestellung — Schritt {schritt} von 3</DialogTitle>
          <DialogDescription>
            {schritt === 1
              ? "Lieferant, Datum und Nummer festlegen"
              : schritt === 2
                ? "Positionen wählen — Artikelsuche oder offene Vormerkungen"
                : "Aus Datei aufbereiten — Vorschau prüfen, dann übernehmen"}
          </DialogDescription>
        </DialogHeader>

        {schritt === 1 ? (
          <div className="flex-1 space-y-4 py-2 overflow-y-auto min-h-0 px-1">
            <div className="space-y-1">
              <label htmlFor="ass-lieferant" className="text-xs font-medium text-ink">Lieferant (Pflicht)</label>
              <select
                id="ass-lieferant"
                value={supplierId ?? ""}
                onChange={(e) => lieferantWechseln(e.target.value === "" ? null : Number(e.target.value))}
                className="w-full rounded border border-line bg-background px-3 py-1.5 text-sm text-ink"
              >
                <option value="">— bitte wählen —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="ass-datum" className="text-xs font-medium text-ink">Bestelldatum</label>
                <Input
                  id="ass-datum"
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                />
                <p className="text-[11px] text-ink-2">Auch in der Vergangenheit — für Nacherfassung.</p>
              </div>
              <div className="space-y-1">
                <label htmlFor="ass-status" className="text-xs font-medium text-ink">Status</label>
                <select
                  id="ass-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as "entwurf" | "bestellt")}
                  className="w-full rounded border border-line bg-background px-3 py-1.5 text-sm text-ink"
                >
                  <option value="bestellt">bestellt</option>
                  <option value="entwurf">Entwurf</option>
                </select>
              </div>
            </div>

            <div className="space-y-2 rounded border border-line p-3">
              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={nacherfassung}
                  onChange={(e) => setNacherfassung(e.target.checked)}
                />
                Bestellnummer selbst vergeben (Nacherfassung)
              </label>
              {nacherfassung ? (
                <div className="space-y-1">
                  <Input
                    aria-label="Bestellnummer"
                    placeholder="z. B. 161082026161"
                    value={nummer}
                    onChange={(e) => setNummer(e.target.value)}
                  />
                  {!nummerPruefung.gueltig && nummer !== "" && (
                    <p role="alert" className="text-xs text-coral">{nummerPruefung.meldung}</p>
                  )}
                  {nummerPruefung.gueltig && nummer !== "" && (
                    <p className="text-xs text-ink-2">Laufende Nummer {nummerPruefung.seq}</p>
                  )}
                </div>
              ) : (
                // Kein Vorschauwert — siehe Kopfkommentar, Punkt 3.
                <p className="text-xs text-ink-2">Nummer: wird automatisch vergeben</p>
              )}
            </div>

            <div className="space-y-1">
              <label htmlFor="ass-notiz" className="text-xs font-medium text-ink">Notiz (optional)</label>
              <Input id="ass-notiz" value={notiz} onChange={(e) => setNotiz(e.target.value)} />
            </div>
          </div>
        ) : schritt === 2 ? (
          <div className="flex-1 space-y-4 py-2 overflow-y-auto min-h-0 px-1">
            <div className="space-y-1">
              <label htmlFor="ass-suche" className="text-xs font-medium text-ink">Artikel suchen</label>
              <Input
                id="ass-suche"
                placeholder="Sternnummer, Lagernummer oder Name"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
              />
              {(suchErgebnis?.items ?? []).length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded border border-line">
                  {(suchErgebnis?.items ?? []).map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-panel-2"
                      onClick={() =>
                        zeileHinzu({
                          key: `hand:${t.id}`,
                          stock_item_id: t.id,
                          merchant_sku: t.merchant_sku ?? null,
                          name: t.name,
                          ausverkauf: t.ausverkauf,
                          ausverkauf_quelle: t.ausverkauf_quelle,
                          qty: 1,
                          unit_price_usd: null,
                          notiz: "",
                          aus_datei: false,
                        })
                      }
                    >
                      <span>{t.merchant_sku ?? "—"} · {t.name}</span>
                      <AusverkaufAbzeichen {...t} />
                      <Plus className="size-3.5" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {offeneVormerkungen.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-ink">
                  Offene Vormerkungen ({offeneVormerkungen.length}) — übernehmen schließt sie
                </div>
                <div className="max-h-32 overflow-y-auto rounded border border-line">
                  {offeneVormerkungen.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      className="flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-panel-2"
                      onClick={() =>
                        zeileHinzu({
                          key: `notiz:${n.id}`,
                          stock_item_id: n.stock_item_id,
                          merchant_sku: n.merchant_sku ?? null,
                          name: n.name,
                          qty: n.quantity ?? 1,
                          unit_price_usd: null,
                          notiz: "",
                          aus_datei: false,
                          note_id: n.id,
                        })
                      }
                    >
                      <span>{n.merchant_sku ?? "—"} · {n.name} · {n.quantity ?? "?"}</span>
                      <Plus className="size-3.5" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {zeilen.length === 0 ? (
              <div className="py-4 text-center text-sm text-ink-2">
                Noch keine Positionen — über die Suche oder eine Vormerkung hinzufügen.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-ink-2">
                      <th className="py-1 pr-2">Sternnummer</th>
                      <th className="py-1 pr-2">Artikel</th>
                      <th className="py-1 pr-2 text-right">Menge</th>
                      {zeilenHabenPreise && <th className="py-1 pr-2 text-right">EK (USD)</th>}
                      <th className="py-1 pr-2">Anforderung</th>
                      <th className="py-1" />
                    </tr>
                  </thead>
                  <tbody>
                    {zeilen.map((z) => (
                      <tr key={z.key} className="border-b border-line/50">
                        <td className="py-1 pr-2"><GrundartikelLink code={z.merchant_sku} /></td>
                        <td className="py-1 pr-2 whitespace-nowrap">{z.name} <AusverkaufAbzeichen {...z} /></td>
                        <td className="py-1 pr-2 text-right">
                          <Input
                            aria-label={`Menge ${z.merchant_sku ?? z.stock_item_id}`}
                            className={`h-7 w-24 text-right inline-block ${mengeOk(z) ? "" : "border-coral"}`}
                            aria-invalid={!mengeOk(z)}
                            type="number"
                            min={1}
                            value={z.qty}
                            onChange={(e) =>
                              setZeilen((alt) =>
                                alt.map((a) =>
                                  a.key === z.key
                                    ? { ...a, qty: Number(e.target.value), set_bindung: null, paar_bestaetigt: false }
                                    : a,
                                ),
                              )
                            }
                          />
                        </td>
                        {zeilenHabenPreise && (
                          <td className="py-1 pr-2 text-right tabular-nums">
                            {z.unit_price_usd === null ? "—" : formatStueckpreisUsd(z.unit_price_usd)}
                          </td>
                        )}
                        <td className="py-1 pr-2 min-w-[120px]">
                          <Input
                            aria-label={`Anforderung ${z.merchant_sku ?? z.stock_item_id}`}
                            className="h-7 w-full"
                            value={z.notiz}
                            onChange={(e) =>
                              setZeilen((alt) =>
                                alt.map((a) =>
                                  a.key === z.key ? { ...a, notiz: e.target.value } : a,
                                ),
                              )
                            }
                          />
                        </td>
                        <td className="py-1 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Position ${z.merchant_sku ?? z.stock_item_id} entfernen`}
                            onClick={() => setZeilen((alt) => alt.filter((a) => a.key !== z.key))}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {status === "bestellt" && zeilen.length === 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Eine bestellte Bestellung braucht mindestens eine Position.
              </p>
            )}
            {ungueltigeMengen.length > 0 && (
              // Selbst gefunden: bisher wurde nur der Knopf gesperrt. Bei mehreren Zeilen
              // sieht man dann NICHT, welche gemeint ist — der Nutzer sucht den Fehler bei
              // sich. Eine Menge wie „2,5" wird zu NaN und faellt sonst stumm durch.
              <p role="alert" className="text-xs text-coral">
                {ungueltigeMengen.length === 1 ? "Eine Position hat" : `${ungueltigeMengen.length} Positionen haben`}
                {" "}keine gültige Menge (ganze Zahl größer als 0):{" "}
                {ungueltigeMengen.map((z) => z.merchant_sku ?? z.name).join(", ")}
              </p>
            )}
          </div>
        ) : (
          <div className="flex-1 space-y-4 py-2 overflow-y-auto min-h-0 px-1">
            <div className="space-y-2 rounded border border-line p-3">
              <label htmlFor="ass-datei" className="text-xs font-medium text-ink">
                Datei mit den Positionen (CSV, Excel XLS/XLSX oder PDF)
              </label>
              {/* Design §118: die Auskunft ueber die Uebertragung steht VOR dem Absenden.
                  Nachtraeglich waere sie wertlos — die Datei waere dann schon unterwegs.
                  Der Gegensatz ist ausdruecklich benannt, sonst muesste der Nutzer annehmen,
                  er gelte fuer jede Datei.
                  Der zweite Halbsatz nennt bewusst KEINEN Tageszustand: der Satz muss stimmen,
                  waehrend der Server PDF noch abweist, UND danach. Was gerade gilt, sagt die
                  Antwort des Servers — sie wird unveraendert angezeigt. */}
              <p className="text-[11px] text-ink-2">
                PDF wird zur Auswertung an einen KI-Dienst übertragen; ist die Auswertung nicht
                verfügbar, meldet der Server das. CSV und Excel werden ausschließlich auf dem
                eigenen Server gelesen.
              </p>
              {/* Das native Feld bleibt im Baum und behaelt seine Beschriftung — nur
                  seine Darstellung ist ausgeblendet. Es zu ERSETZEN (etwa durch einen
                  Knopf, der `showOpenFilePicker` ruft) haette Tastatur- und
                  Vorlesebedienung gekostet; `sr-only` laesst beides unveraendert.
                  Grund fuer den sichtbaren Knopf: die native Darstellung heisst je nach
                  Browser und Sprache „Datei auswählen", „Choose File" oder „Durchsuchen"
                  und ist nicht gestaltbar — Nutzer haben sie schlicht nicht gefunden. */}
              <input
                ref={dateiFeld}
                id="ass-datei"
                type="file"
                accept=".csv,.xls,.xlsx,.pdf"
                disabled={vorschlagLaeuft}
                className="sr-only"
                onChange={(e) => {
                  const eingabe = e.currentTarget;
                  const datei = eingabe.files?.[0];
                  // Wert leeren, NACHDEM die Datei gegriffen ist: sonst loest dieselbe
                  // Datei nach einem Fehlschlag kein `change` mehr aus und der Nutzer
                  // haelt den Assistenten fuer kaputt. (Befund von Reviewer 1.)
                  eingabe.value = "";
                  if (datei) void dateiAuswerten(datei);
                }}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={vorschlagLaeuft}
                  onClick={() => dateiFeld.current?.click()}
                >
                  {dateiName === null ? "Durchsuchen" : "Andere Datei auswählen"}
                </Button>
                <span className="text-xs text-ink-2">
                  {dateiName ?? "Keine Datei ausgewählt"}
                </span>
              </div>
              <p className="text-[11px] text-ink-2">
                Die Datei wird nur gelesen und ausgewertet. Es wird nichts gespeichert, bis du
                die Bestellung im letzten Schritt anlegst.
              </p>
              {vorschlagLaeuft && <p className="text-xs text-ink-2">Datei wird gelesen…</p>}
              {/* Beide Meldebereiche stehen DAUERHAFT im Baum und bleiben leer, solange es
                  nichts zu sagen gibt. Ein Bereich, der erst mitsamt seinem Text eingehaengt
                  wird, wird von verbreiteten Vorleseprogrammen nicht verlaesslich angesagt —
                  sie beobachten den Inhalt einer bestehenden Region. Getrennt statt einer
                  Region mit wechselnder Rolle: eine Rolle im laufenden Betrieb zu tauschen
                  ist genauso unzuverlaessig. (Befund beider Reviewer.) */}
              <p role="alert" className="text-xs text-coral empty:hidden">
                {vorschlagMeldung?.art === "fehler" ? vorschlagMeldung.text : ""}
              </p>
              <p role="status" className="text-xs text-amber-700 empty:hidden dark:text-amber-400">
                {vorschlagMeldung?.art === "hinweis" ? vorschlagMeldung.text : ""}
              </p>
            </div>

            {vorschlag && (
              <>
                {vorschlag.warnungen.length > 0 && (
                  <ul className="space-y-1 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200">
                    {vorschlag.warnungen.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                )}

                {/* Modul 43 — die Mengenumrechnung, ROT und nachrechenbar.
                    Rot, obwohl nichts kaputt ist: hier aendert sich stillschweigend
                    die Bestellmenge, und genau diese Stille war der Schaden. Der
                    Kasten zeigt die ganze Rechnung, damit der Einkaeufer den Faktor
                    gegen seine Absprache mit dem Lieferanten pruefen kann. */}
                {brauchtBestaetigung && (
                  <div
                    role="alert"
                    className="space-y-2 rounded border border-coral bg-coral/5 p-3 text-xs text-coral"
                  >
                    <p className="font-medium">
                      {umgerechneteZeilen.length === 1
                        ? "Eine Zeile wird in Stück umgerechnet"
                        : `${umgerechneteZeilen.length} Zeilen werden in Stück umgerechnet`}
                      {vorschlag.lieferanten_profil
                        ? ` (Profil „${vorschlag.lieferanten_profil}")`
                        : ""}
                      :
                    </p>
                    <ul className="space-y-1">
                      {umgerechneteZeilen.map((pos) => (
                        <li key={pos.zeile} className="tabular-nums">
                          Zeile {pos.zeile}
                          {pos.raw_sku ? ` · ${pos.raw_sku}` : ""}: {umrechnungsSatz(pos)}
                        </li>
                      ))}
                    </ul>
                    <label className="flex items-start gap-2 font-medium">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={einheitBestaetigt}
                        onChange={(e) => setEinheitBestaetigt(e.currentTarget.checked)}
                      />
                      <span>
                        Ich habe die Umrechnung geprüft und bestätige sie für diese Datei
                        {dateiName ? ` (${dateiName})` : ""}.
                      </span>
                    </label>
                  </div>
                )}

                {/* Plan 2026-10-21 (B3): pcs-Zeilen auf Paar-/Set-Artikel — nie still umrechnen. */}
                {paarFragen.length > 0 && (
                  <div
                    role="group"
                    aria-label="Paare gezählt?"
                    className="space-y-2 rounded border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
                  >
                    <p className="font-medium">
                      {paarFragen.length === 1
                        ? "Eine Zeile zählt in Stück, der Artikel wird aber als Paar/Set geführt"
                        : `${paarFragen.length} Zeilen zählen in Stück, die Artikel werden aber als Paar/Set geführt`}
                      {offenePaarFragen.length > 0 ? ` — noch ${offenePaarFragen.length} offen` : ""}:
                    </p>
                    {sammelGruppen.map(([n, zeilenNr]) => (
                      <Button
                        key={n}
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setPaarAntwort((alt) => {
                            const neu = { ...alt };
                            for (const nr of zeilenNr) neu[nr] = "paar";
                            return neu;
                          })
                        }
                      >
                        Alle markierten ({zeilenNr.length}): {paarWorte(n).ja}
                      </Button>
                    ))}
                    <ul className="space-y-1.5">
                      {paarFragen.map((pos) => {
                        const frage = paarFrageFuer(pos)!;
                        const worte = paarWorte(frage.stueck_je_set);
                        const antwort = paarAntwort[pos.zeile];
                        const dateiPreis = pos.source_unit_price_usd ?? pos.unit_price_usd;
                        const preise = [
                          frage.letzter_ek_usd !== null ? `letzter EK ${formatStueckpreisUsd(frage.letzter_ek_usd)}` : null,
                          dateiPreis !== null ? `Datei ${formatStueckpreisUsd(dateiPreis)}` : null,
                        ].filter((t): t is string => t !== null);
                        return (
                          <li key={pos.zeile} className="flex flex-wrap items-center gap-2 tabular-nums">
                            <span className="flex-1 min-w-[220px]">
                              Zeile {pos.zeile}
                              {pos.raw_sku ? ` · ${pos.raw_sku}` : ""}:{" "}
                              {formatMenge(pos.source_qty ?? pos.qty ?? 0)} {pos.source_unit ?? "Stück"} — {worte.frage}{" "}
                              ({[worte.einheit, ...preise].join(", ")})
                              {frage.empfehlung === "paar" && (
                                <strong> · Preis spricht für {frage.stueck_je_set === 2 ? "Paare" : "Sets"}</strong>
                              )}
                              {frage.empfehlung === "stueck" && <strong> · Preis spricht für Stück</strong>}
                              {frage.als_paar === null && (
                                <strong className="block">
                                  {frage.stueck_je_set === 2 ? "Paare" : "Sets"} nicht übernehmbar
                                  {frage.sperre ? `: ${frage.sperre}` : ""} — bitte „Nein, Stück" bestätigen oder
                                  die Menge von Hand klären.
                                </strong>
                              )}
                              {antwort === "paar" && frage.als_paar !== null && (
                                <span className="block">
                                  → {formatMenge(frage.als_paar.qty)} Stück
                                  {frage.als_paar.unit_price_usd !== null
                                    ? ` @ ${formatStueckpreisUsd(frage.als_paar.unit_price_usd)} USD`
                                    : ""}
                                </span>
                              )}
                            </span>
                            <Button
                              size="sm"
                              variant={antwort === "paar" ? "default" : "outline"}
                              aria-pressed={antwort === "paar"}
                              aria-label={`Zeile ${pos.zeile}: ${worte.ja}`}
                              disabled={frage.als_paar === null}
                              onClick={() => setPaarAntwort((alt) => ({ ...alt, [pos.zeile]: "paar" }))}
                            >
                              {worte.ja}
                            </Button>
                            <Button
                              size="sm"
                              variant={antwort === "stueck" ? "default" : "outline"}
                              aria-pressed={antwort === "stueck"}
                              aria-label={`Zeile ${pos.zeile}: Nein, Stück`}
                              onClick={() => setPaarAntwort((alt) => ({ ...alt, [pos.zeile]: "stueck" }))}
                            >
                              Nein, Stück
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Gesperrte Zeilen halten die ganze Uebernahme an — nicht nur sich selbst. */}
                {gesperrteZeilen.length > 0 && (
                  <div role="alert" className="space-y-1 rounded border border-coral bg-coral/5 p-3 text-xs text-coral">
                    <p className="font-medium">
                      {gesperrteZeilen.length === 1
                        ? "Eine Position konnte nicht sicher gedeutet werden"
                        : `${gesperrteZeilen.length} Positionen konnten nicht sicher gedeutet werden`}
                      {" "}— solange sie in der Datei stehen, wird nichts übernommen:
                    </p>
                    <ul className="space-y-1">
                      {gesperrteZeilen.map((pos) => (
                        <li key={pos.zeile}>
                          Zeile {pos.zeile}
                          {pos.raw_sku ? ` · ${pos.raw_sku}` : ""}: {pos.blocked_reason ?? "Grund unbekannt."}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-1">
                  <div className="text-xs font-medium text-ink">Erkannte Spalten</div>
                  {/* Hier stand ein Auswahlfeld je Feld. Es hat nie gewirkt und konnte es
                      auch nicht: `POST /orders/vorschlag/` nimmt ausschliesslich die Datei
                      entgegen (routes.ts), und der Browser haelt die Rohzeilen gar nicht —
                      er koennte selbst nicht umhaengen. Ein Bedienelement ohne Wirkung ist
                      dieselbe Attrappe, die in der Kopfleiste entfernt wurde (A9); es sagt
                      dem Nutzer zu, was niemand einloest. Angezeigt wird die Erkennung
                      trotzdem — wer sie falsch findet, sieht wenigstens WORAN es lag.
                      Eine echte Umzuordnung braucht einen Parameter am Endpunkt (P10). */}
                  <div className="grid gap-2 sm:grid-cols-2">
                    {Object.entries(vorschlag.spalten.erkannt).map(([feld, spalte]) => (
                      <div key={feld} className="flex items-center justify-between gap-2 text-xs text-ink-2">
                        <span>{feld}</span>
                        <span className="text-ink">{spalte}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-ink-2">
                    Stimmt die Zuordnung nicht, hilft eine Datei mit klaren Spaltenüberschriften —
                    die Auswertung nimmt zurzeit keine geänderte Zuordnung entgegen.
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-line text-left text-xs text-ink-2">
                        <th className="py-1 pr-2">Zeile</th>
                        <th className="py-1 pr-2">Aus der Datei</th>
                        <th className="py-1 pr-2 text-right">Menge</th>
                        {vorschlagHatPreise && <th className="py-1 pr-2 text-right">EK (USD)</th>}
                        <th className="py-1 pr-2">Artikel in der Plattform</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vorschlag.positionen.map((pos) => (
                        <tr
                          key={pos.zeile}
                          className={
                            pos.blocked === true
                              ? "border-b border-line/50 bg-coral/5 text-coral"
                              : paarFrageFuer(pos) !== null && paarAntwort[pos.zeile] === undefined
                                ? "border-b border-line/50 bg-amber-50 dark:bg-amber-950/30"
                                : "border-b border-line/50"
                          }
                        >
                          <td className="py-1 pr-2 text-ink-2">{pos.zeile}</td>
                          <td className="py-1 pr-2 whitespace-nowrap">
                            {pos.raw_sku ? <span className="font-medium">{pos.raw_sku} · </span> : null}
                            {pos.raw_name ?? "—"}
                          </td>
                          <td className="py-1 pr-2 text-right tabular-nums">
                            {pos.qty ?? "—"}
                            {/* Die Quellmenge steht DANEBEN, nicht statt der Bestellmenge:
                                der Einkaeufer haelt die Rechnung in der Hand und sucht die
                                Zahl, die dort steht. Ohne sie waere die Tabelle nicht mehr
                                mit dem Papier abzugleichen. */}
                            {pos.requires_unit_confirmation === true
                              && pos.source_qty !== null && pos.source_qty !== undefined && (
                              <span className="block text-[11px] font-normal text-coral">
                                aus {formatMenge(pos.source_qty)} {pos.source_unit ?? ""}
                              </span>
                            )}
                          </td>
                          {vorschlagHatPreise && (
                            <td className="py-1 pr-2 text-right tabular-nums">
                              {pos.unit_price_usd === null ? "—" : formatStueckpreisUsd(pos.unit_price_usd)}
                              {pos.requires_unit_confirmation === true
                                && pos.source_unit_price_usd !== null
                                && pos.source_unit_price_usd !== undefined && (
                                <span className="block text-[11px] font-normal text-coral">
                                  aus {formatStueckpreisUsd(pos.source_unit_price_usd)}/{pos.source_unit ?? ""}
                                </span>
                              )}
                            </td>
                          )}
                          <td className="py-1 pr-2 min-w-[200px]">
                            {pos.blocked === true ? (
                              <span className="text-xs">gesperrt — nicht übernehmbar</span>
                            ) : pos.match === "exact" && pos.stock_item_id !== null ? (
                              <span className="text-xs">eindeutig zugeordnet</span>
                            ) : (
                              <select
                                aria-label={`Artikel für Zeile ${pos.zeile}`}
                                value={wahl[pos.zeile] ?? ""}
                                onChange={(e) => {
                                  setWahl((alt) => ({
                                    ...alt,
                                    [pos.zeile]: e.target.value === "" ? null : Number(e.target.value),
                                  }));
                                  // Die Antwort gehoerte zum vorher gewaehlten Artikel.
                                  setPaarAntwort((alt) => {
                                    const rest = { ...alt };
                                    delete rest[pos.zeile];
                                    return rest;
                                  });
                                }}
                                className="w-full rounded border border-line bg-background px-2 py-0.5 text-xs text-ink"
                              >
                                <option value="">— bitte zuordnen —</option>
                                {pos.kandidaten.map((k) => (
                                  <option key={k.stock_item_id} value={k.stock_item_id}>
                                    {k.merchant_sku ?? k.sku ?? k.stock_item_id} · {k.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {offeneZuordnungen > 0 && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {offeneZuordnungen} {offeneZuordnungen === 1 ? "Zeile ist" : "Zeilen sind"} noch
                    ohne Artikel — sie {offeneZuordnungen === 1 ? "wird" : "werden"} nicht übernommen.
                  </p>
                )}
                {vorbereiteteUebernahme.zusammengefassteZeilen > 0 && (
                  <p className="text-xs text-ink-2">
                    {vorbereiteteUebernahme.zusammengefassteZeilen} doppelte Dateizeile(n) mit
                    gleichem Artikel, EK und Einheit werden beim Übernehmen summiert.
                  </p>
                )}
                {vorbereiteteUebernahme.mehrfach.length > 0 && (
                  <ul className="space-y-0.5 text-xs text-ink-2" aria-label="Mehrere Positionen je Artikel">
                    {vorbereiteteUebernahme.mehrfach.map((text) => (
                      <li key={text}>{text} — Misch-EK gewichtet beim Einlagern</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 shrink-0 flex-wrap">
          <Button variant="outline" size="sm" onClick={schliessen}>Abbrechen</Button>
          {schritt === 1 ? (
            <Button size="sm" disabled={kopfFehlt} onClick={() => setSchritt(2)}>
              Weiter zu den Positionen
            </Button>
          ) : schritt === 2 ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setSchritt(1)}>Zurück</Button>
              <Button variant="outline" size="sm" onClick={() => setSchritt(3)}>Aus Datei aufbereiten</Button>
              <Button size="sm" disabled={!darfAbsenden || laeuft} onClick={() => void absenden()}>
                {laeuft ? "Wird angelegt…" : "Bestellung anlegen"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setSchritt(2)}>Zurück</Button>
              <Button
                size="sm"
                disabled={uebernahmeGesperrt}
                onClick={vorschlagUebernehmen}
              >
                {vorbereiteteUebernahme.zeilen.length > 0
                  ? `${vorbereiteteUebernahme.zeilen.length} Positionen übernehmen`
                  : "Positionen übernehmen"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
